// Vendor
import moment from 'moment';

// Personal libs
import { TokenTypeEnum } from '@shared/enum/token-type.enum';
import { extractTimeDataFromSeconds } from '@shared/lib/date.lib';
import { SessionToken } from './session-token.abstract';
import { dbGetActiveUser } from '@data/datasource/local/user.database';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { IAuthResponse } from '@shared/interface/auth-response.interface';
import { IApiGetTokensParams } from '@data/datasource/remote/auth.api';
import authRepository from '@data/repository/auth.repository';
import { ApiErrorLabel } from '@shared/enum/api-error-label.enum';
import { ApiError } from '@shared/error/api.error';
import JWT from '@shared/class/jwt.class';
import { TokenStatusEnum } from '@shared/enum/token-status.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { displayNotifWarning } from '@shared/lib/notify.lib';

export class AccessToken extends SessionToken {
  /**
   * Constructor
   */
  public constructor() {
    super(TokenTypeEnum.ACCESS_TOKEN);
  }

  /**
   * Overriden methods
   */
  async refreshTokenData() {
    const jwt = new JWT();
    // await jwt.refreshFromLocalDb();

    if (jwt.isValid()) {
      const exp = jwt.getAdvancedExp();
      this.remainingTime.value = exp.seconds - moment().unix();
      if (this.remainingTime.value < 0) {
        this.remainingTime.value = 0;
      }
      this.remainingDataTime.value = extractTimeDataFromSeconds(
        this.remainingTime.value,
      );
    } else {
      this.remainingTime.value = undefined;
      this.remainingDataTime.value = undefined;
    }
  }

  async refreshStatus(): Promise<void> {
    if (this.remainingTime.value == null || this.remainingTime.value == 0) {
      this.tokenStatus.value = TokenStatusEnum.INACTIVE;
    } else {
      this.tokenStatus.value = TokenStatusEnum.ACTIVE;
    }
  }

  async refresh(): Promise<IApiResponse<IAuthResponse, string> | undefined> {
    const activeUser = await dbGetActiveUser();

    if (activeUser?.isConnected === true && !this.isRefreshing) {
      this.isRefreshing = true;

      const params: IApiGetTokensParams = {
        chkRemember: activeUser?.chkRemember === true,
        uuid: activeUser?.uuid ?? '',
        refreshToken: activeUser?.refreshToken?.token ?? '',
        fingerprint: activeUser?.refreshToken?.fingerprint ?? ''
      };

      const refreshTokenRetries = (sessionManager.refreshToken.getRetries()) ?? 0;
      const refreshTokenTtl = (sessionManager.refreshToken.getTtl()) ?? 0;

      let refreshTokenRetriesCpt = 0;
      let nbFailed = 0;
      let response: IApiResponse<IAuthResponse, string> | undefined;

      while (refreshTokenRetriesCpt < refreshTokenRetries) {
        try {
          response = await authRepository.refreshTokens(params);
          refreshTokenRetriesCpt = refreshTokenRetries;
        } catch (error) {
          this.log(`refresh - error : ${error}`);
          refreshTokenRetriesCpt++;

          if (error instanceof ApiError) {
            if (process.env.DEBUG == true) {
              displayNotifWarning(
                `Refresh token error : ${error.code ?? 'no code'} - ${error.message ?? 'no message'}`,
                {
                  position: 'bottom',
                  timeout: 0,
                  group: error.code
                }
              )
            }
            if (!error.message.includes(ApiErrorLabel.NetworkError)) {
              nbFailed++;
            }
          } else if (error instanceof Error) {
            if (process.env.DEBUG == true) {
              displayNotifWarning(
                `Refresh token error : ${error.message ?? 'no message'}`,
                {
                  position: 'bottom',
                  timeout: 0
                }
              )
            }
            if (error.message.includes(ApiErrorLabel.NetworkError)) {
              this.isRefreshing = false;
              throw new Error(ApiErrorLabel.NetworkError);
            } else {
              nbFailed++;
            }
          }
        }
      }

      this.isRefreshing = false;

      if (
        sessionManager.isOnline.value &&
        nbFailed >= refreshTokenRetries &&
        !(await this.isValid())
      ) {
        sessionManager.askSessionExpired(refreshTokenTtl);

        return undefined;
      } else {
        await sessionManager.login();

        return response;
      }
    } else {
      throw new Error();
    }
  }

  clearToken(): void {
    const jwt = new JWT();
    jwt.clearCookies();

    this.remainingDataTime.value = undefined;
    this.remainingTime.value = undefined;

    this.tokenStatus.value = TokenStatusEnum.INACTIVE;
  }
}
